import React from 'react';
import { StyleSheet, TextStyle, View, ViewStyle } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import Illustration, {
  IllustrationSourceType,
} from 'shared/components/vxl/Illustration/Illustration';
import BodyXSBoldText from 'shared/components/vxl/typography/primary/body/BodyXSBoldText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { isWeb } from 'shared/constants/platform';
import { PressableAnalyticsProps } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

type VXLMarketingCardColorTheme =
  | 'lavender'
  | 'lime'
  | 'sunset'
  | 'forest'
  | 'mint'
  | 'eggplant';

export interface VXLMarketingCardProps extends PressableAnalyticsProps {
  labelText: string;
  headlineText: string;
  illustration: IllustrationSourceType;
  onPress: () => void;
  disabled?: boolean;
  accessibilityLabel?: string;
  testID?: string;
  colorTheme?: VXLMarketingCardColorTheme;
}

const HEADLINE_MAX_LINES = 3;
const LABEL_TEXT_MAX_LINES = 1;
const ARROW_ICON_SIZE = 32;
const ILLUSTRATION_SIZE = 60;
const CARD_HEIGHT = 198;
const CARD_WIDTH = 300;

const VXLMarketingCard = ({
  colorTheme = 'lavender',
  labelText,
  headlineText,
  illustration,
  onPress,
  disabled = false,
  accessibilityLabel = `${labelText}. ${headlineText}`,
  testID = '',
  analyticsId = undefined,
  customAttributes = null,
}: VXLMarketingCardProps) => {
  const themeStyles = colorThemeStyles[colorTheme];

  return (
    <PressableWithDelay
      style={[styles.container, themeStyles.background]}
      onPress={onPress}
      // TODO: Remove when focusVisible is available in a future RN upgrade.
      onPressOut={(e) => {
        if (isWeb) {
          e.currentTarget.blur();
        }
      }}
      accessible
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      testID={testID}
      disabled={disabled}
      analyticsId={analyticsId}
      customAttributes={customAttributes}
    >
      {({ hovered, pressed, focused }) => (
        <>
          <View style={styles.textContainer}>
            <BodyXSBoldText
              style={themeStyles.labelTextColor}
              numberOfLines={LABEL_TEXT_MAX_LINES}
            >
              {labelText}
            </BodyXSBoldText>
            <TitleMdText
              style={themeStyles.headlineTextColor}
              numberOfLines={HEADLINE_MAX_LINES}
            >
              {headlineText}
            </TitleMdText>
          </View>
          <View style={styles.arrowButtonIllustrationContainer}>
            <View
              style={[
                styles.arrowButtonContainer,
                hovered && styles.hovered,
                // Only show focus ring for keyboard navigation, not pointer clicks. TODO: Replace with focusVisible once available in a future RN upgrade.
                focused && !hovered && !pressed && styles.focused,
                pressed && styles.pressed,
                disabled && styles.disabled,
              ]}
            >
              <Icon
                iconKey="ARROW_RIGHT"
                iconSize="SM"
                iconColor={
                  disabled
                    ? theme.color.text.tertiary
                    : theme.color.interactive.primary
                }
              />
            </View>
            <View style={styles.illustrationContainer}>
              <Illustration illustration={illustration} />
            </View>
          </View>
        </>
      )}
    </PressableWithDelay>
  );
};

const styles = StyleSheet.create({
  arrowButtonContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    borderRadius: ARROW_ICON_SIZE / 2,
    height: ARROW_ICON_SIZE,
    justifyContent: 'center',
    width: ARROW_ICON_SIZE,
  },
  arrowButtonIllustrationContainer: {
    alignItems: 'flex-end',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  container: {
    borderRadius: 4,
    gap: theme.spacing.xs,
    height: CARD_HEIGHT,
    justifyContent: 'space-between',
    padding: theme.spacing.m,
    width: CARD_WIDTH,
  },
  disabled: {
    backgroundColor: theme.color.interactive.disabled.background,
  },
  focused: {
    outlineColor: theme.color.border.focused,
    outlineStyle: 'solid',
    outlineWidth: 1,
  },
  hovered: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  illustrationContainer: {
    height: ILLUSTRATION_SIZE,
    width: ILLUSTRATION_SIZE,
  },
  pressed: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
  textContainer: {
    gap: theme.spacing.s,
    paddingRight: theme.spacing.m,
  },
});

interface ColorThemeStyle {
  background: ViewStyle;
  labelTextColor: TextStyle;
  headlineTextColor: TextStyle;
}

const colorThemeStyles: Record<VXLMarketingCardColorTheme, ColorThemeStyle> = {
  lavender: StyleSheet.create({
    background: {
      backgroundColor: theme.color.expressive.lavender,
    },
    labelTextColor: {
      color: theme.color.text.primary,
    },
    headlineTextColor: {
      color: theme.color.expressive.eggplant,
    },
  }),
  sunset: StyleSheet.create({
    background: {
      backgroundColor: theme.color.expressive.sunset,
    },
    labelTextColor: {
      color: theme.color.text.primary,
    },
    headlineTextColor: {
      color: theme.color.text.inverse,
    },
  }),
  lime: StyleSheet.create({
    background: {
      backgroundColor: theme.color.expressive.lime,
    },
    labelTextColor: {
      color: theme.color.text.primary,
    },
    headlineTextColor: {
      color: theme.color.expressive.forest,
    },
  }),
  forest: StyleSheet.create({
    background: {
      backgroundColor: theme.color.expressive.forest,
    },
    labelTextColor: {
      color: theme.color.text.inverse,
    },
    headlineTextColor: {
      color: theme.color.expressive.lime,
    },
  }),
  mint: StyleSheet.create({
    background: {
      backgroundColor: theme.color.expressive.mint,
    },
    labelTextColor: {
      color: theme.color.text.primary,
    },
    headlineTextColor: {
      color: theme.color.text.inverse,
    },
  }),
  eggplant: StyleSheet.create({
    /* eslint-disable react-native/no-unused-styles */
    background: {
      backgroundColor: theme.color.expressive.eggplant,
    },
    labelTextColor: {
      color: theme.color.text.inverse,
    },
    headlineTextColor: {
      color: theme.color.expressive.sky,
    },
  }),
};

export default VXLMarketingCard;
